import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Linkedin, Calendar } from "lucide-react";
import CalendlyInline from "./CalendlyInline";

export default function Footer() {
  return (
    <footer data-testid="main-footer" className="bg-brand-ink text-white relative overflow-hidden">
      {/* Bandeau "Prendre rendez-vous" avec Calendly intégré au-dessus du footer principal */}
      <div className="relative bg-white text-brand-ink border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 lg:gap-14 items-start">
            <div>
              <div className="overline">Prendre rendez-vous</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 leading-tight">
                Réservez votre <span className="italic font-editorial">audit offert</span>.
              </h2>
              <p className="mt-5 text-brand-ink/70 text-[15px] leading-relaxed max-w-md font-light">
                30 minutes pour faire le point sur vos contrats. Confidentiel, sans engagement.
                Je vous propose des créneaux en visio ou téléphone.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+33615907316"
                  data-testid="footer-rdv-call"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-black text-white px-6 py-3.5 text-[12px] font-semibold tracking-[0.2em] uppercase"
                >
                  <Phone className="h-4 w-4" /> Appeler maintenant
                </a>
                <Link
                  to="/rendez-vous"
                  data-testid="footer-rdv-page"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-6 py-3.5 text-[12px] font-semibold tracking-[0.2em] uppercase transition-colors"
                >
                  <Calendar className="h-4 w-4" /> Voir la page complète
                </Link>
              </div>
            </div>
            <div className="rounded-2xl border border-brand-border overflow-hidden bg-white shadow-[0_10px_30px_rgba(17,17,17,0.05)]">
              <CalendlyInline minHeight={640} testId="footer-calendly-widget" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 grain opacity-30" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="mb-7">
            <img
              src="/logos/optina-white.svg"
              alt="OPTINA"
              className="h-12 w-auto"
            />
          </div>
          <p className="font-editorial italic text-xl md:text-2xl leading-snug text-white/85 max-w-md">
            Je compare. Vous économisez.
          </p>
          <p className="mt-5 text-sm text-white/65 max-w-md leading-relaxed">
            Courtier indépendant en assurance et énergie. J'analyse, je négocie et j'optimise vos contrats — sans frais, sans engagement.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              data-testid="footer-instagram"
              href="https://www.instagram.com/courtier.optina"
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 w-10 rounded-full border border-white/20 hover:bg-white hover:text-brand-ink flex items-center justify-center transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              data-testid="footer-linkedin"
              href="https://www.linkedin.com/in/nassim-aali"
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 w-10 rounded-full border border-white/20 hover:bg-white hover:text-brand-ink flex items-center justify-center transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <div className="overline text-white mb-4">Navigation</div>
          <ul className="space-y-2 text-sm text-white/75">
            <li><Link to="/" className="hover:text-white">Accueil</Link></li>
            <li><Link to="/assurance" className="hover:text-white">Assurance</Link></li>
            <li><Link to="/assurance/professionnelle" className="hover:text-white">Assurance professionnelle</Link></li>
            <li><Link to="/assurance/emprunteur" className="hover:text-white">Assurance emprunteur</Link></li>
            <li><Link to="/energie" className="hover:text-white">Énergie</Link></li>
            <li><Link to="/epargne-patrimoine" className="hover:text-white">Épargne &amp; PER</Link></li>
            <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
            <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline text-white mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                <strong className="text-white font-semibold">Au cœur d'Aurillac</strong>
                <span className="block text-white/60 text-[12.5px] mt-0.5">Sur rendez-vous</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                <strong className="text-white font-semibold">Paris</strong>
                <span className="block text-white/60 text-[12.5px] mt-0.5">
                  78 Avenue des Champs-Élysées, Bureau 326, 75008
                  <span className="block">Sur rendez-vous</span>
                </span>
              </span>
            </li>
            <li className="flex items-center gap-3"><Phone className="h-4 w-4" /><a data-testid="footer-phone" href="tel:+33615907316" className="hover:text-white">+33 6 15 90 73 16</a></li>
            <li className="flex items-center gap-3"><Mail className="h-4 w-4" /><a href="mailto:nassim@optina.fr" className="hover:text-white">nassim@optina.fr</a></li>
          </ul>
          <p className="mt-5 text-[12.5px] text-white/60 leading-relaxed italic">
            Je peux vous recevoir à Aurillac, en centre-ville, ou me déplacer à votre domicile ou
            sur votre lieu de travail. Échanges et souscription à distance possibles partout en
            France.
          </p>
        </div>
      </div>

      {/* Carte GPS Aurillac — zone d'intervention */}
      <div className="relative border-t border-white/10 bg-black/20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
          <div className="grid md:grid-cols-[1fr_2fr] gap-8 items-center">
            <div>
              <div className="overline text-white/80">Ma zone d'intervention</div>
              <p className="mt-3 text-white text-[15px] leading-relaxed">
                Aurillac, ses alentours et tout le département du Cantal — Paris et l'ensemble du
                territoire français à distance.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/15 shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
              <iframe
                title="Carte Aurillac — Zone d'intervention OPTINA"
                src="https://www.google.com/maps?q=Aurillac%2C+Cantal%2C+France&output=embed&z=11"
                width="100%"
                height="240"
                style={{ border: 0, display: "block", filter: "grayscale(70%) contrast(1.05)" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="footer-map-aurillac"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bandeau de confiance ORIAS + ACPR */}
      <div
        data-testid="footer-trust-band"
        className="relative border-t border-white/10 bg-black/30"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <div className="flex items-center gap-5 md:gap-6 shrink-0 flex-wrap">
            <a
              href="https://www.orias.fr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Vérifier l'inscription ORIAS d'OPTINA"
              className="inline-flex items-center justify-center bg-white rounded-md px-4 py-3 transition-opacity hover:opacity-80"
              data-testid="footer-orias-logo"
            >
              <img
                src="/logos/orias.svg"
                alt="ORIAS — Registre unique des intermédiaires en assurance"
                className="h-12 md:h-14 w-auto"
              />
            </a>
            <div
              className="inline-flex items-center justify-center bg-white rounded-md px-4 py-3"
              data-testid="footer-acpr-badge"
              aria-label="Sous le contrôle de l'ACPR — Banque de France"
            >
              <img
                src="/logos/acpr.webp"
                alt="ACPR — Autorité de Contrôle Prudentiel et de Résolution · Banque de France"
                className="h-12 md:h-14 w-auto"
              />
            </div>
          </div>
          <p className="text-[12.5px] text-white/75 leading-relaxed">
            OPTINA — Courtier immatriculé à l'ORIAS n°&nbsp;26008264, sous le contrôle de l'ACPR
            (Autorité de Contrôle Prudentiel et de Résolution — 4 Place de Budapest, 75436 Paris Cedex 09).
            <br className="hidden md:block" />
            Vous pouvez vérifier notre immatriculation sur{" "}
            <a
              href="https://www.orias.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-white hover:text-white"
            >
              orias.fr
            </a>
            .
          </p>
        </div>
      </div>

      {/* Bloc mentions légales obligatoires courtier — complet */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 text-[11.5px] text-white/65 leading-relaxed space-y-1">
          <p className="text-white font-semibold tracking-wide">OPTINA — Nassim Aali</p>
          <p>Courtier en assurance — Entreprise individuelle</p>
          <p>78 avenue des Champs-Élysées, Bureau 326 — 75008 Paris</p>
          <p>SIREN : 933 396 277 — RCS Paris</p>
          <p>
            ORIAS n°&nbsp;26008264 (
            <a href="https://www.orias.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
              www.orias.fr
            </a>
            )
          </p>
          <p>
            Adhérent VotrAsso — Sous le contrôle de l'ACPR (4 Place de Budapest, 75436 Paris Cedex 09)
          </p>
          <p>
            Médiation : La Médiation de l'Assurance —{" "}
            <a href="https://www.mediation-assurance.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
              www.mediation-assurance.org
            </a>
          </p>
          <p>
            <a href="mailto:nassim@optina.fr" className="underline hover:text-white">nassim@optina.fr</a> —{" "}
            <a href="tel:+33615907316" className="underline hover:text-white">06 15 90 73 16</a>
          </p>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 grid md:grid-cols-2 gap-6 text-[11px] text-white/55 leading-relaxed">
          <div data-testid="footer-legal">
            <p>
              OPTINA — Courtier en assurance · ORIAS n°&nbsp;26008264 (
              <a
                href="https://www.orias.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              >
                www.orias.fr
              </a>
              ) ·{" "}
              <Link
                to="/mentions-legales"
                className="underline hover:text-white"
                data-testid="footer-orias-link"
              >
                Mentions légales
              </Link>
            </p>
          </div>
          <div className="md:text-right text-white/55">
            © 2026 OPTINA · Tous droits réservés ·{" "}
            <Link to="/mentions-legales" className="hover:text-white" data-testid="footer-mentions-legales">Mentions légales</Link> ·{" "}
            <Link to="/politique-confidentialite" className="hover:text-white" data-testid="footer-politique-confidentialite">Politique de confidentialité</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
